import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { RouterLink } from '@angular/router';

/**
 * Home pubblica del sito: video intro alla prima visita, poi la pagina si divide
 * nelle due aree Gateway e Ecosystem for Regulators.
 * Le visite successive (stesso browser) arrivano direttamente alle due aree.
 */
@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './landing-page.component.html',
  styleUrl: './landing-page.component.scss'
})
export class LandingPageComponent implements AfterViewInit, OnDestroy {
  @ViewChild('introVideo') private introVideo!: ElementRef<HTMLVideoElement>;

  /** true per mostrare l'intro a ogni visita. */
  private readonly introEveryVisit = false;
  private readonly storageKey = 'gw_intro_seen';
  /** Pausa sull'ultimo fotogramma prima della divisione. */
  private readonly holdLastFrameMs = 900;
  private readonly fadeOutMs = 600;

  introVisible = false;
  introLeaving = false;
  entering = false;

  private startTimer?: ReturnType<typeof setTimeout>;
  private holdTimer?: ReturnType<typeof setTimeout>;
  private hideTimer?: ReturnType<typeof setTimeout>;

  ngAfterViewInit(): void {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!reduceMotion && (this.introEveryVisit || !this.introSeen())) {
      // Fuori dal ciclo di change detection corrente
      this.startTimer = setTimeout(() => this.playIntro());
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.startTimer);
    clearTimeout(this.holdTimer);
    clearTimeout(this.hideTimer);
    document.body.style.overflow = '';
  }

  playIntro(): void {
    clearTimeout(this.holdTimer);
    clearTimeout(this.hideTimer);
    this.introVisible = true;
    this.introLeaving = false;
    document.body.style.overflow = 'hidden';

    const video = this.introVideo.nativeElement;
    // L'attributo muted nel template non imposta la proprietà: senza questa riga
    // il browser considera il video con audio e blocca l'autoplay.
    video.muted = true;
    try { video.currentTime = 0; } catch { /* non ancora caricato */ }
    video.play()?.catch(() => this.showSplit(false)); // autoplay bloccato
  }

  onIntroEnded(): void {
    this.holdTimer = setTimeout(() => this.showSplit(true), this.holdLastFrameMs);
  }

  showSplit(animate: boolean): void {
    clearTimeout(this.holdTimer);
    this.markIntroSeen();

    // Riavvia l'animazione CSS di ingresso delle due aree
    this.entering = false;
    if (animate) {
      setTimeout(() => this.entering = true);
    }

    if (this.introVisible) {
      this.introLeaving = true;
      this.hideTimer = setTimeout(() => {
        this.introVisible = false;
        this.introLeaving = false;
        this.introVideo.nativeElement.pause();
      }, animate ? this.fadeOutMs : 0);
    }
    document.body.style.overflow = '';
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.introVisible) {
      this.showSplit(true);
    }
  }

  private introSeen(): boolean {
    try { return localStorage.getItem(this.storageKey) === '1'; } catch { return false; }
  }

  private markIntroSeen(): void {
    try { localStorage.setItem(this.storageKey, '1'); } catch { /* modalità privata: si ignora */ }
  }
}
